<template>
  <div class="three-axis-sensor-chart">
    <LineChart class="line-chart" :chart-data="chartData" />
    <div class="sensor-chart-wait-spinner" v-if="!hasSensorData">
      <i class="fa-3x fas fa-spinner fa-spin"></i>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
} from 'vue';
import LineChart from './LineChart.vue';
import type { LineChartProps } from 'src/models/chart';
import type {
  ActiveElement,
  Chart,
  ChartData,
  ChartEvent,
  ChartOptions,
  ChartType,
  TooltipItem,
} from 'chart.js';
import { dtFormat, ensureDate } from 'src/lib/dateHelper';
import type { SensorData } from 'src/models/mtx';

interface ThreeAxisSensorChartProps {
  sensorData: SensorData[];
}

const props = defineProps<ThreeAxisSensorChartProps>();

const emit = defineEmits(['click']);

const hasSensorData = computed(() => {
  return props.sensorData.length > 0;
});

const formatSensorData = (sensorData: SensorData[]) => {
  const xLabels: Date[] = [];
  const vs: number[] = [];
  const xs: number[] = [];
  const ys: number[] = [];
  const zs: number[] = [];
  sensorData.forEach(obj => {
    const startDate = ensureDate(obj.startTs);
    if (!startDate) return;

    // ds, vs, xs, ys, zsは全て同じlengthのはず..を軽くチェック
    const len = obj.ds.length;
    if (len !== obj.vs.length || len !== obj.xs.length || len !== obj.ys.length || len !== obj.zs.length) return;

    let current = startDate.valueOf();
    obj.ds.forEach((d) => {
      const dt = new Date(current + d);
      xLabels.push(dt);
      current += d;
    });
    vs.push(...obj.vs);
    xs.push(...obj.xs);
    ys.push(...obj.ys);
    zs.push(...obj.zs);
  });

  return { xLabels, vs, xs, ys, zs };
};

const chartData = computed<LineChartProps>(() => {
  const { xLabels, vs, xs, ys, zs } = formatSensorData(props.sensorData);
  const chartConfig: ChartData = {
    labels: xLabels,
    datasets: [
      {
        data: vs,
        type: 'line',
        yAxisID: 'y-axis-velocity',
        label: '速度',
        fill: true,
        tension: 0.1,
        backgroundColor: 'rgba(75,192,192,0.4)',
        borderColor: 'rgba(75,192,192,1)',
        borderWidth: 1,
        borderCapStyle: 'butt',
        borderJoinStyle: 'miter',
        pointBackgroundColor: 'rgba(75,192,192,1)',
        pointBorderWidth: 0,
        pointHoverRadius: 3,
        pointHoverBackgroundColor: 'rgba(75,192,192,1)',
        pointHoverBorderColor: 'rgba(220,220,220,1)',
        pointHoverBorderWidth: 2,
        pointRadius: 0,
        pointHitRadius: 10,
        spanGaps: false,
      },
      {
        data: xs,
        type: 'line',
        yAxisID: 'y-axis-gsensor',
        label: 'X軸',
        fill: false,
        tension: 0,
        backgroundColor: 'rgba(214,69,65,0.4)',
        borderColor: 'rgba(214,69,65,1)',
        borderWidth: 1,
        borderCapStyle: 'butt',
        borderJoinStyle: 'miter',
        pointBackgroundColor: 'rgba(214,69,65,1)',
        pointBorderWidth: 0,
        pointHoverRadius: 3,
        pointHoverBackgroundColor: 'rgba(214,69,65,1)',
        pointHoverBorderColor: 'rgba(220,220,220,1)',
        pointHoverBorderWidth: 2,
        pointRadius: 0,
        pointHitRadius: 10,
        spanGaps: false,
      },
      {
        data: ys,
        type: 'line',
        yAxisID: 'y-axis-gsensor',
        label: 'Y軸',
        fill: false,
        tension: 0,
        backgroundColor: 'rgba(52,152,219,0.4)',
        borderColor: 'rgba(52,152,219,1)',
        borderWidth: 1,
        borderCapStyle: 'butt',
        borderJoinStyle: 'miter',
        pointBackgroundColor: 'rgba(52,152,219,1)',
        pointBorderWidth: 0,
        pointHoverRadius: 3,
        pointHoverBackgroundColor: 'rgba(52,152,219,1)',
        pointHoverBorderColor: 'rgba(220,220,220,1)',
        pointHoverBorderWidth: 2,
        pointRadius: 0,
        pointHitRadius: 10,
        spanGaps: false,
      },
      {
        data: zs,
        type: 'line',
        yAxisID: 'y-axis-gsensor',
        label: 'Z軸',
        fill: false,
        tension: 0,
        backgroundColor: 'rgba(233,212,96,0.4)',
        borderColor: 'rgba(233,212,96,1)',
        borderWidth: 1,
        borderCapStyle: 'butt',
        borderJoinStyle: 'miter',
        pointBackgroundColor: 'rgba(233,212,96,1)',
        pointBorderWidth: 0,
        pointHoverRadius: 3,
        pointHoverBackgroundColor: 'rgba(233,212,96,1)',
        pointHoverBorderColor: 'rgba(220,220,220,1)',
        pointHoverBorderWidth: 1,
        pointRadius: 0,
        pointHitRadius: 10,
        spanGaps: false,
      },
    ],
  };
  const opts: ChartOptions = {
    layout: {
      padding: {
        top: 0,
        right: 0,
        bottom: 12,
        left: 0,
      },
    },
    animation: {
      duration: 0,
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        position: 'nearest',
        callbacks: {
          title: (tooltipItemArr: TooltipItem<ChartType>[]) => {
            const ti = tooltipItemArr[0];
            if (ti.dataIndex === undefined) return '';

            const dt = xLabels[ti.dataIndex];
            return dtFormat(dt, 'H:MM:SS.sss');
          },
          label: (tooltipItem: TooltipItem<ChartType>) => {
            const datasetIdx = tooltipItem.datasetIndex;
            const dataset = tooltipItem.dataset;
            const val = tooltipItem.formattedValue;
            let ret;
            if (datasetIdx > 0) {
              ret = `${dataset.label}: ${val}`;
            } else {
              ret = `${dataset.label}: ${val} km/h`;
            }
            return ret;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        position: 'top',
        time: {
          minUnit: 'minute',
          displayFormats: {
            hour: 'H:mm',
            minute: 'H:mm',
          },
        },
      },
      'y-axis-velocity': {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        title: {
          display: true,
          text: 'km/h',
        },
      },
      'y-axis-gsensor': {
        type: 'linear',
        position: 'right',
        suggestedMin: -1.5,
        suggestedMax: 1.5,
        title: {
          display: true,
          text: '3軸センサー',
        },
      },
    },
    onClick: (_event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
      if (elements.length === 0) return;

      const elem = elements[0];
      const rawChartData = chart.data;
      const barIndex = elem.index;
      const xLabel = rawChartData.labels?.[barIndex];
      emit('click', xLabel);
    },
  };
  return { data: chartConfig, opts };
});
</script>

<style lang="scss" scoped>
.line-chart {
  height: 100%;
}
.sensor-chart-wait-spinner {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  .fa-spinner {
    align-self: center;
  }
}
</style>
